import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './AuthLogs.css';

const PMS_LABELS = { capitaland:'ODX', odx:'ODX', winhms:'WinHMS', wincloud:'WinCloud', idsnext:'IDS Next', idx:'IDS Next' };

const TIMELINE_ICONS = {
    AUTH_SESSION_CREATED:          '🔑',
    USER_CONNECTED:                '📶',
    NAS_IDENTIFIED:                '🔍',
    AUTH_URL_CREATED:              '🔗',
    USER_DETAILS_RECEIVED:         '📝',
    PMS_SELECTED:                  '🏨',
    PMS_REQUEST_SENT:              '📤',
    PMS_RESPONSE_RECEIVED:         '📥',
    PMS_VERIFICATION_SUCCESS:      '✅',
    PMS_VERIFICATION_FAILED:       '❌',
    HOTSPOT_AUTHORIZATION_SUCCESS: '✅',
    HOTSPOT_AUTHORIZATION_FAILED:  '❌',
    auto_rejected:                 '🚫',
    approval_pending:              '⏳',
    approval_approved:             '✅',
    approval_rejected:             '❌',
};

function fmt(date) {
    if (!date) return '—';
    return new Date(date).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true, second: '2-digit' });
}

function fmtMs(ms) {
    if (!ms && ms !== 0) return '—';
    return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(2)}s`;
}

export default function AuthLogDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [doc, setDoc] = useState(null);
    const [tab, setTab] = useState('timeline');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/auth-logs/${id}`)
            .then(r => setDoc(r.data.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) return <div className="al-wrap"><p className="al-loading">Loading…</p></div>;
    if (!doc)    return <div className="al-wrap"><p className="al-empty">Session not found.</p></div>;

    const r = doc.result || {};
    const authStatus = r.authorizationStatus || 'PENDING';

    return (
        <div className="al-wrap">
            <div className="al-header">
                <button className="al-back" onClick={() => navigate('/auth-logs')}>← Back</button>
                <h1 className="al-title">{doc.authSessionId}</h1>
                <span className={`al-badge al-badge-${authStatus.toLowerCase()}`}>{authStatus}</span>
            </div>

            {/* Top summary cards */}
            <div className="ald-summary">
                <Info label="Correlation ID"  value={doc.correlationId} mono />
                <Info label="Auth Session ID" value={doc.authSessionId}  mono />
                <Info label="Property"        value={doc.branchName}          />
                <Info label="PMS"             value={PMS_LABELS[doc.pms?.provider] || doc.pms?.provider} />
                <Info label="Room"            value={doc.guest?.roomNumber}   />
                <Info label="Guest"           value={doc.guest?.surname || doc.guest?.email} />
                <Info label="MAC"             value={doc.network?.mac} mono   />
                <Info label="IP"              value={doc.network?.ip}  mono   />
                <Info label="NAS"             value={doc.nas?.nasIdentifier || doc.nas?.nasIp} />
                <Info label="PMS Duration"    value={fmtMs(doc.pms?.durationMs)} />
                <Info label="Started"         value={fmt(doc.connectedAt)}    />
                <Info label="Completed"       value={fmt(doc.completedAt)}    />
            </div>

            {/* Tabs */}
            <div className="ald-tabs">
                {['timeline', 'session', 'pms', 'request', 'response', 'result'].map(t => (
                    <button key={t} className={`ald-tab${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                    </button>
                ))}
            </div>

            <div className="ald-body">
                {tab === 'timeline' && <TimelineTab events={doc.timeline || []} />}
                {tab === 'session'  && <SessionTab  doc={doc} />}
                {tab === 'pms'      && <PmsTab      pms={doc.pms} />}
                {tab === 'request'  && <JsonView    data={doc.pms?.requestSnapshot}  title="PMS Request" />}
                {tab === 'response' && <JsonView    data={doc.pms?.responseSnapshot} title="PMS Response" />}
                {tab === 'result'   && <ResultTab   result={doc.result} />}
            </div>
        </div>
    );
}

function TimelineTab({ events }) {
    if (!events.length) return <p className="al-empty">No timeline events recorded.</p>;
    return (
        <div className="ald-timeline">
            {events.map((e, i) => (
                <div key={i} className={`ald-event ald-event-${(e.status || 'info').toLowerCase()}`}>
                    <div className="ald-event-icon">{TIMELINE_ICONS[e.event] || '●'}</div>
                    <div className="ald-event-body">
                        <div className="ald-event-name">{e.event}</div>
                        {e.message && <div className="ald-event-msg">{e.message}</div>}
                        <div className="ald-event-time">{fmt(e.timestamp)}{e.durationMs ? ` · ${fmtMs(e.durationMs)}` : ''}</div>
                    </div>
                    <span className={`al-badge al-badge-${(e.status||'info').toLowerCase()}`}>{e.status || 'INFO'}</span>
                </div>
            ))}
        </div>
    );
}

function SessionTab({ doc }) {
    return (
        <div className="ald-section">
            <Row label="Correlation ID"   value={doc.correlationId} mono />
            <Row label="Auth Session ID"  value={doc.authSessionId}  mono />
            <Row label="Tenant ID"        value={doc.tenantId}       mono />
            <Row label="Branch ID"        value={doc.branchId}       mono />
            <Row label="Property"         value={doc.branchName} />
            <Row label="Room"             value={doc.guest?.roomNumber} />
            <Row label="Surname"          value={doc.guest?.surname} />
            <Row label="User Type"        value={doc.guest?.userType} />
            <Row label="Email"            value={doc.guest?.email} />
            <Row label="MAC"              value={doc.network?.mac} mono />
            <Row label="IP"               value={doc.network?.ip}  mono />
            <Row label="User Agent"       value={doc.network?.userAgent} />
            <Row label="NAS Identifier"   value={doc.nas?.nasIdentifier} />
            <Row label="NAS IP"           value={doc.nas?.nasIp} mono />
            <Row label="Link Original"    value={doc.authUrl?.linkOrig} />
            <Row label="Connected At"     value={fmt(doc.connectedAt)} />
            <Row label="Completed At"     value={fmt(doc.completedAt)} />
        </div>
    );
}

function PmsTab({ pms }) {
    if (!pms?.provider) return <p className="al-empty">No PMS data recorded.</p>;
    return (
        <div className="ald-section">
            <Row label="Provider"     value={PMS_LABELS[pms.provider] || pms.provider} />
            <Row label="Operation"    value={pms.operation} />
            <Row label="Login Type"   value={pms.loginType} />
            <Row label="Endpoint"     value={pms.endpoint} mono />
            <Row label="HTTP Method"  value={pms.method} />
            <Row label="HTTP Status"  value={pms.httpStatus} />
            <Row label="Duration"     value={fmtMs(pms.durationMs)} />
            <Row label="Attempts"     value={pms.attempt} />
            <Row label="Request ID"   value={pms.requestId} mono />
        </div>
    );
}

function ResultTab({ result }) {
    if (!result) return <p className="al-empty">No result recorded.</p>;
    return (
        <div className="ald-section">
            <Row label="Verification Status"  value={result.verificationStatus} />
            <Row label="Authorization Status" value={result.authorizationStatus} />
            <Row label="Error Code"           value={result.errorCode} />
            <Row label="Error Message"        value={result.errorMessage} />
            <Row label="Session ID"           value={result.sessionId} mono />
            <Row label="User ID"              value={result.userId}    mono />
        </div>
    );
}

function JsonView({ data, title }) {
    const [copied, setCopied] = useState(false);
    if (!data) return <p className="al-empty">No {title} data recorded.</p>;
    const text = JSON.stringify(data, null, 2);
    function copy() {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    }
    return (
        <div className="ald-json-wrap">
            <div className="ald-json-header">
                <span>{title}</span>
                <button className="ald-copy" onClick={copy}>{copied ? '✓ Copied' : 'Copy'}</button>
            </div>
            <pre className="ald-json">{text}</pre>
        </div>
    );
}

function Info({ label, value, mono }) {
    return (
        <div className="ald-info">
            <span className="ald-info-label">{label}</span>
            <span className={`ald-info-value${mono ? ' al-mono' : ''}`}>{value || '—'}</span>
        </div>
    );
}

function Row({ label, value, mono }) {
    return (
        <div className="ald-row">
            <span className="ald-row-label">{label}</span>
            <span className={`ald-row-value${mono ? ' al-mono' : ''}`}>{value || '—'}</span>
        </div>
    );
}
